import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAlerts, type AlertSeverity } from '../hooks/useAlerts'

const SEVERITY_STYLES: Record<AlertSeverity, { container: string; icon: typeof Info }> = {
  success: {
    container:
      'bg-green-50 border-green-500 text-green-900 dark:bg-green-900/20 dark:border-green-600 dark:text-green-100',
    icon: CheckCircle2,
  },
  warning: {
    container:
      'bg-yellow-50 border-yellow-500 text-yellow-900 dark:bg-yellow-900/20 dark:border-yellow-600 dark:text-yellow-100',
    icon: AlertTriangle,
  },
  error: {
    container:
      'bg-red-50 border-red-500 text-red-900 dark:bg-red-900/20 dark:border-red-600 dark:text-red-100',
    icon: XCircle,
  },
  // Not an authoring option, but valid in the database — render neutral, never as an error.
  info: {
    container:
      'bg-blue-50 border-blue-500 text-blue-900 dark:bg-blue-900/20 dark:border-blue-600 dark:text-blue-100',
    icon: Info,
  },
}

/**
 * Global alert bar — broadcast messages from admin, shown on every page.
 * Three levels: green (success), yellow (warning), red (error).
 */
export default function AlertBar() {
  const { t } = useTranslation('components')
  const { alerts, dismiss } = useAlerts()

  if (alerts.length === 0) return null

  return (
    <div data-testid="alert-bar">
      {alerts.map((alert) => {
        const style = SEVERITY_STYLES[alert.severity] ?? SEVERITY_STYLES.info
        const Icon = style.icon
        return (
          <div
            key={alert.id}
            data-testid={`alert-${alert.id}`}
            data-severity={alert.severity}
            role={alert.severity === 'error' ? 'alert' : 'status'}
            className={`flex items-start gap-3 border-b-2 px-6 py-3 ${style.container}`}
          >
            <Icon className="w-5 h-5 mt-0.5 shrink-0" aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold">{alert.title}</p>
              <p className="text-sm whitespace-pre-line wrap-break-word">{alert.content}</p>
            </div>
            <button
              type="button"
              onClick={() => dismiss(alert)}
              aria-label={t('alertBar.dismiss', { title: alert.title })}
              className="shrink-0 p-1 rounded-sm hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
